/**
 * Mixed Fleet Optimizer
 * 
 * Implements priority-based algorithms for optimizing fleets that serve
 * both fixed routes and on-demand transit services simultaneously.
 */

import type { FixedRoute } from '../../types/fixedRoutes';
import type { RideSchema } from '../../types/simulation';
import type { Line } from '../../context/context';
import type { MixedFleetConfig, FleetResult, VehicleAssignment } from './fleetTypes';
import type { BreakPeriod } from '../../types/simulation';
import { FleetOptimizer } from './fleetOptimizer';
import { parseTime, calculateTimeDifference, addMinutes, formatTime } from '../../components/map/components/auto-fleet-generator/timeUtils';

/**
 * Calculate mixed fleet using fixed-first priority
 * 
 * @param routes - Array of fixed routes
 * @param trips - Array of on-demand trips
 * @param config - Mixed fleet configuration
 * @returns Fleet result with mixed assignments
 */
export function calculateFixedFirst(
  routes: FixedRoute[],
  trips: RideSchema[] | Line[],
  config: MixedFleetConfig
): FleetResult {
  const optimizer = new FleetOptimizer();
  
  // Step 1: Calculate fixed route fleet using Phase 1 logic
  const fixedRouteConfig = {
    routes: config.routes,
    vehicleSharingMode: config.vehicleSharingMode,
    vehicleSpecs: config.vehicleSpecs,
    trafficMultiplier: config.trafficMultiplier,
  };
  
  const fixedRouteResult = optimizer.calculateFixedRouteFleet(routes, fixedRouteConfig);
  
  // Step 2: Calculate on-demand requirements
  const tripsPerVehiclePerHour = 5; // Default, should come from config if available
  const hourlyDemand = optimizer.buildHourlyDemandTimeline(trips);
  const onDemandVehicleCount = optimizer.calculateOnDemandVehicleRequirements(trips, tripsPerVehiclePerHour);
  const serviceHours = optimizer.identifyOnDemandServicePeriods(trips);
  
  // Step 3: Identify gaps and remaining capacity in fixed route assignments
  const fixedAssignments = fixedRouteResult.vehicleAssignments;
  const gaps = identifyGapsInFixedRouteAssignments(fixedAssignments);
  
  // Step 4: Allocate on-demand to gaps and additional vehicles
  const mixedAssignments = allocateOnDemandToFixedRoutes(
    fixedAssignments,
    trips,
    serviceHours,
    onDemandVehicleCount,
    gaps,
    config.transitionTimeMinutes || 0,
    config
  );
  
  // Step 5: Build summary
  const summary = buildMixedFleetSummary(
    mixedAssignments,
    fixedRouteResult.vehicleCount,
    onDemandVehicleCount,
    'fixed-first',
    routes.length,
    serviceHours
  );
  
  return {
    vehicleCount: Math.max(fixedRouteResult.vehicleCount, mixedAssignments.length),
    vehicleAssignments: mixedAssignments,
    summary: {
      totalVehicles: mixedAssignments.length,
      routesServed: routes.length,
      operatingHoursCoverage: `${serviceHours.start} - ${serviceHours.end}`,
      sharingMode: config.vehicleSharingMode,
    },
    mixedFleetSummary: summary,
  };
}

/**
 * Calculate mixed fleet using on-demand-first priority
 * 
 * @param routes - Array of fixed routes
 * @param trips - Array of on-demand trips
 * @param config - Mixed fleet configuration
 * @returns Fleet result with mixed assignments
 */
export function calculateOnDemandFirst(
  routes: FixedRoute[],
  trips: RideSchema[] | Line[],
  config: MixedFleetConfig
): FleetResult {
  const optimizer = new FleetOptimizer();
  
  // Step 1: Calculate on-demand fleet requirements
  const tripsPerVehiclePerHour = 5; // Default
  const serviceHours = optimizer.identifyOnDemandServicePeriods(trips);
  const onDemandResult = optimizer.calculateOnDemandFleet(trips, {
    mode: 'trip-layer',
    manualVehicleCount: optimizer.calculateOnDemandVehicleRequirements(trips, tripsPerVehiclePerHour),
    vehicleSharingMode: 'dedicated',
    serviceStartTime: serviceHours.start || config.serviceStartTime || '06:00',
    serviceEndTime: serviceHours.end || config.serviceEndTime || '22:00',
    maxShiftTimeMinutes: 480,
    breakLengthMinutes: 30,
    minTimeBeforeBreakMinutes: 120,
    minTimeAfterBreakMinutes: 120,
    standardSeats: config.vehicleSpecs.standardSeats || 10,
    wheelchairSeats: config.vehicleSpecs.wheelchairSeats || 1,
    startLocationId: config.vehicleSpecs.startLocationId || '',
    endLocationId: config.vehicleSpecs.endLocationId || '',
    peakPeriods: [],
    breakReductionDuringPeakMinutes: 0,
    staggerBreaks: true,
    tripsPerVehiclePerHour,
    useDemandBasedAllocation: config.useDemandBasedAllocation || false,
    hourlyVehicleRequirements: config.hourlyVehicleRequirements,
    flexibilityPercent: 0,
  });
  
  // Step 2: Calculate fixed route requirements
  const fixedRouteConfig = {
    routes: config.routes,
    vehicleSharingMode: config.vehicleSharingMode,
    vehicleSpecs: config.vehicleSpecs,
    trafficMultiplier: config.trafficMultiplier,
  };
  
  const fixedRouteResult = optimizer.calculateFixedRouteFleet(routes, fixedRouteConfig);
  
  // Step 3: Identify available capacity for fixed routes
  const onDemandAssignments = onDemandResult.vehicleAssignments;
  const gaps = identifyGapsInOnDemandAssignments(onDemandAssignments);
  
  // Step 4: Allocate fixed routes to gaps
  const mixedAssignments = allocateFixedRoutesToOnDemand(
    onDemandAssignments,
    fixedRouteResult.vehicleAssignments,
    gaps,
    config.transitionTimeMinutes || 0
  );
  
  // Step 5: Build summary
  const summary = buildMixedFleetSummary(
    mixedAssignments,
    fixedRouteResult.vehicleCount,
    onDemandResult.vehicleCount,
    'on-demand-first',
    routes.length,
    serviceHours
  );
  
  return {
    vehicleCount: Math.max(onDemandResult.vehicleCount, mixedAssignments.length),
    vehicleAssignments: mixedAssignments,
    summary: {
      totalVehicles: mixedAssignments.length,
      routesServed: routes.length,
      operatingHoursCoverage: `${serviceHours.start} - ${serviceHours.end}`,
      sharingMode: config.vehicleSharingMode,
    },
    mixedFleetSummary: summary,
  };
}

/**
 * Calculate mixed fleet using equal priority
 * 
 * @param routes - Array of fixed routes
 * @param trips - Array of on-demand trips
 * @param config - Mixed fleet configuration
 * @returns Fleet result with mixed assignments
 */
export function calculateEqualPriority(
  routes: FixedRoute[],
  trips: RideSchema[] | Line[],
  config: MixedFleetConfig
): FleetResult {
  const optimizer = new FleetOptimizer();
  
  // Step 1: Calculate requirements for both service types separately
  const fixedRouteConfig = {
    routes: config.routes,
    vehicleSharingMode: config.vehicleSharingMode,
    vehicleSpecs: config.vehicleSpecs,
    trafficMultiplier: config.trafficMultiplier,
  };
  
  const fixedRouteResult = optimizer.calculateFixedRouteFleet(routes, fixedRouteConfig);
  const tripsPerVehiclePerHour = 5;
  const onDemandVehicleCount = optimizer.calculateOnDemandVehicleRequirements(trips, tripsPerVehiclePerHour);
  const serviceHours = optimizer.identifyOnDemandServicePeriods(trips);
  
  // Step 2: Build unified timeline of all service requirements
  const unifiedTimeline = buildUnifiedTimeline(
    fixedRouteResult.vehicleAssignments,
    trips,
    serviceHours,
    onDemandVehicleCount
  );
  
  // Step 3: Optimize vehicle allocation to minimize total vehicles
  const mixedAssignments = optimizeUnifiedAllocation(
    unifiedTimeline,
    fixedRouteResult.vehicleAssignments,
    trips,
    serviceHours,
    config.transitionTimeMinutes || 0,
    config
  );
  
  // Step 4: Build summary
  const summary = buildMixedFleetSummary(
    mixedAssignments,
    fixedRouteResult.vehicleCount,
    onDemandVehicleCount,
    'equal',
    routes.length,
    serviceHours
  );
  
  return {
    vehicleCount: mixedAssignments.length,
    vehicleAssignments: mixedAssignments,
    summary: {
      totalVehicles: mixedAssignments.length,
      routesServed: routes.length,
      operatingHoursCoverage: `${serviceHours.start} - ${serviceHours.end}`,
      sharingMode: config.vehicleSharingMode,
    },
    mixedFleetSummary: summary,
  };
}

/**
 * Calculate mixed fleet using weighted priority
 * 
 * @param routes - Array of fixed routes
 * @param trips - Array of on-demand trips
 * @param config - Mixed fleet configuration
 * @returns Fleet result with mixed assignments
 */
export function calculateWeighted(
  routes: FixedRoute[],
  trips: RideSchema[] | Line[],
  config: MixedFleetConfig
): FleetResult {
  const optimizer = new FleetOptimizer();
  
  // Step 1: Calculate weighted vehicle requirements
  const fixedRouteConfig = {
    routes: config.routes,
    vehicleSharingMode: config.vehicleSharingMode,
    vehicleSpecs: config.vehicleSpecs,
    trafficMultiplier: config.trafficMultiplier,
  };
  
  const fixedRouteResult = optimizer.calculateFixedRouteFleet(routes, fixedRouteConfig);
  const tripsPerVehiclePerHour = 5;
  const onDemandVehicleCount = optimizer.calculateOnDemandVehicleRequirements(trips, tripsPerVehiclePerHour);
  const serviceHours = optimizer.identifyOnDemandServicePeriods(trips);
  
  // Normalize weights
  const fixedWeight = config.fixedRouteWeight || 50;
  const onDemandWeight = config.onDemandWeight || 50;
  const totalWeight = fixedWeight + onDemandWeight;
  const normalizedFixedWeight = fixedWeight / totalWeight;
  const normalizedOnDemandWeight = onDemandWeight / totalWeight;
  
  // Step 2: Allocate vehicles proportionally based on weights
  const totalVehiclesNeeded = fixedRouteResult.vehicleCount + onDemandVehicleCount;
  const allocatedFixedVehicles = Math.ceil(totalVehiclesNeeded * normalizedFixedWeight);
  const allocatedOnDemandVehicles = Math.ceil(totalVehiclesNeeded * normalizedOnDemandWeight);
  
  // Step 3: Generate assignments with weighted allocation
  const mixedAssignments = allocateWeightedAssignments(
    fixedRouteResult.vehicleAssignments,
    trips,
    serviceHours,
    allocatedFixedVehicles,
    allocatedOnDemandVehicles,
    config.transitionTimeMinutes || 0,
    config
  );
  
  // Step 4: Build summary
  const summary = buildMixedFleetSummary(
    mixedAssignments,
    fixedRouteResult.vehicleCount,
    onDemandVehicleCount,
    'weighted',
    routes.length,
    serviceHours
  );
  
  return {
    vehicleCount: mixedAssignments.length,
    vehicleAssignments: mixedAssignments,
    summary: {
      totalVehicles: mixedAssignments.length,
      routesServed: routes.length,
      operatingHoursCoverage: `${serviceHours.start} - ${serviceHours.end}`,
      sharingMode: config.vehicleSharingMode,
    },
    mixedFleetSummary: summary,
  };
}

/**
 * Identify gaps in fixed route assignments where on-demand can be allocated
 */
function identifyGapsInFixedRouteAssignments(
  assignments: VehicleAssignment[]
): Array<{ vehicleId: string; start: string; end: string }> {
  const gaps: Array<{ vehicleId: string; start: string; end: string }> = [];
  
  assignments.forEach((assignment) => {
    // For now, identify gaps between route assignments
    // More sophisticated gap detection can be added later
    const routeAssignments = assignment.routeAssignments;
    if (routeAssignments.length === 0) {
      // Entire operating period is available
      gaps.push({
        vehicleId: assignment.vehicleId,
        start: assignment.operatingPeriod.startTime,
        end: assignment.operatingPeriod.endTime,
      });
    } else {
      // Check for gaps between route assignments
      for (let i = 0; i < routeAssignments.length - 1; i++) {
        const currentEnd = parseTime(routeAssignments[i].endTime);
        const nextStart = parseTime(routeAssignments[i + 1].startTime);
        
        if (nextStart > currentEnd) {
          gaps.push({
            vehicleId: assignment.vehicleId,
            start: routeAssignments[i].endTime,
            end: routeAssignments[i + 1].startTime,
          });
        }
      }
      
      // Check for gap after last route assignment
      const lastAssignment = routeAssignments[routeAssignments.length - 1];
      const lastEnd = parseTime(lastAssignment.endTime);
      const shiftEnd = parseTime(assignment.operatingPeriod.endTime);
      
      if (shiftEnd > lastEnd) {
        gaps.push({
          vehicleId: assignment.vehicleId,
          start: lastAssignment.endTime,
          end: assignment.operatingPeriod.endTime,
        });
      }
    }
  });
  
  return gaps;
}

/**
 * Identify gaps in on-demand assignments where fixed routes can be allocated
 */
function identifyGapsInOnDemandAssignments(
  assignments: VehicleAssignment[]
): Array<{ vehicleId: string; start: string; end: string }> {
  // Similar to fixed route gaps, but for on-demand periods
  // For now, return empty gaps as on-demand assignments are typically continuous
  // This can be enhanced to detect actual gaps in on-demand coverage
  return [];
}

/**
 * Allocate on-demand service to gaps in fixed route assignments
 */
function allocateOnDemandToFixedRoutes(
  fixedAssignments: VehicleAssignment[],
  trips: RideSchema[] | Line[],
  serviceHours: { start: string; end: string },
  onDemandVehicleCount: number,
  gaps: Array<{ vehicleId: string; start: string; end: string }>,
  transitionTimeMinutes: number,
  config: MixedFleetConfig
): VehicleAssignment[] {
  const optimizer = new FleetOptimizer();
  
  // Create on-demand assignments
  const onDemandResult = optimizer.calculateOnDemandFleet(trips, {
    mode: 'trip-layer',
    manualVehicleCount: onDemandVehicleCount,
    vehicleSharingMode: 'dedicated',
    serviceStartTime: serviceHours.start || config.serviceStartTime || '06:00',
    serviceEndTime: serviceHours.end || config.serviceEndTime || '22:00',
    maxShiftTimeMinutes: 480,
    breakLengthMinutes: 30,
    minTimeBeforeBreakMinutes: 120,
    minTimeAfterBreakMinutes: 120,
    standardSeats: config.vehicleSpecs.standardSeats || 10,
    wheelchairSeats: config.vehicleSpecs.wheelchairSeats || 1,
    startLocationId: config.vehicleSpecs.startLocationId || '',
    endLocationId: config.vehicleSpecs.endLocationId || '',
    peakPeriods: [],
    breakReductionDuringPeakMinutes: 0,
    staggerBreaks: true,
    tripsPerVehiclePerHour: 5,
    useDemandBasedAllocation: config.useDemandBasedAllocation || false,
    hourlyVehicleRequirements: config.hourlyVehicleRequirements,
    flexibilityPercent: 0,
  });
  
  const onDemandAssignments = onDemandResult.vehicleAssignments as Array<VehicleAssignment & {
    onDemandPeriods?: Array<{ start: string; end: string; breaks: BreakPeriod[]; serviceType: 'on-demand' }>;
  }>;
  
  // Try to merge sequential, non-overlapping assignments
  // Note: vehicleSharingMode relates to sharing between routes, not mixing fixed/on-demand
  // So we allow merging sequential periods even if vehicleSharingMode is 'dedicated'
  const mixedAssignments: VehicleAssignment[] = [...fixedAssignments];
  const remainingOnDemand: typeof onDemandAssignments = [];
  
  // Merge sequential, non-overlapping periods to minimize total vehicles
  {
    // For each on-demand assignment, try to find a fixed route vehicle that can accommodate it
    for (const onDemandAssignment of onDemandAssignments) {
      const onDemandPeriods = onDemandAssignment.onDemandPeriods || [];
      if (onDemandPeriods.length === 0) continue;
      
      // Find the earliest start and latest end of on-demand periods
      let onDemandStart = onDemandPeriods[0].start;
      let onDemandEnd = onDemandPeriods[0].end;
      onDemandPeriods.forEach(period => {
        if (compareTimes(period.start, onDemandStart) < 0) {
          onDemandStart = period.start;
        }
        if (compareTimes(period.end, onDemandEnd) > 0) {
          onDemandEnd = period.end;
        }
      });
      
      const onDemandStartMinutes = parseTime(onDemandStart);
      const onDemandEndMinutes = parseTime(onDemandEnd);
      
      console.log(`[Mixed Fleet Optimizer] Checking on-demand assignment ${onDemandAssignment.vehicleName}: ${onDemandStart}-${onDemandEnd} (${onDemandStartMinutes}-${onDemandEndMinutes} minutes)`);
      
      let merged = false;
      
      // Try to find a fixed route vehicle with sequential periods
      for (let i = 0; i < mixedAssignments.length; i++) {
        const fixedAssignment = mixedAssignments[i];
        
        // Skip if this fixed assignment already has on-demand periods merged (already merged)
        const existingAssignment = fixedAssignment as VehicleAssignment & {
          onDemandPeriods?: Array<{ start: string; end: string; breaks: BreakPeriod[]; serviceType: 'on-demand' }>;
        };
        if (existingAssignment.onDemandPeriods && existingAssignment.onDemandPeriods.length > 0) {
          // This assignment already has on-demand merged, skip it
          continue;
        }
        
        // For fixed routes, check the actual route assignment times, not just operating period
        // Find the earliest route assignment start time
        let fixedStart = parseTime(fixedAssignment.operatingPeriod.startTime);
        let fixedEnd = parseTime(fixedAssignment.operatingPeriod.endTime);
        
        if (fixedAssignment.routeAssignments.length > 0) {
          // Use the earliest route assignment start time
          fixedStart = Math.min(fixedStart, ...fixedAssignment.routeAssignments.map(ra => parseTime(ra.startTime)));
          // Use the latest route assignment end time
          fixedEnd = Math.max(fixedEnd, ...fixedAssignment.routeAssignments.map(ra => parseTime(ra.endTime)));
        }
        
        console.log(`[Mixed Fleet Optimizer] Comparing against fixed assignment ${fixedAssignment.vehicleName}: operatingPeriod ${fixedAssignment.operatingPeriod.startTime}-${fixedAssignment.operatingPeriod.endTime}, route assignments: ${fixedAssignment.routeAssignments.map(ra => `${ra.startTime}-${ra.endTime}`).join(', ')}, calculated fixedStart=${fixedStart}, fixedEnd=${fixedEnd}`);
        
        // Check for sequential periods (on-demand ends right before fixed starts, or vice versa)
        const transitionBuffer = transitionTimeMinutes;
        
        console.log(`[Mixed Fleet Optimizer] Comparison: onDemandEnd=${onDemandEndMinutes}, fixedStart=${fixedStart}, transitionBuffer=${transitionBuffer}, condition=${onDemandEndMinutes} <= ${fixedStart + transitionBuffer} = ${onDemandEndMinutes <= fixedStart + transitionBuffer}`);
        
        // Case 1: On-demand ends before fixed starts (sequential)
        // Allow exact match or within transition buffer (on-demand ends exactly when fixed starts)
        // Merge if on-demand ends before or exactly when fixed starts (allowing for transition buffer)
        // This allows merging when on-demand ends exactly when fixed starts, or with small gaps up to transition buffer
        if (onDemandEndMinutes <= fixedStart + transitionBuffer) {
          // Merge: combine on-demand periods into fixed route vehicle
          const existingAssignment = mixedAssignments[i] as VehicleAssignment & {
            onDemandPeriods?: Array<{ start: string; end: string; breaks: BreakPeriod[]; serviceType: 'on-demand' }>;
          };
          const existingOnDemandPeriods = existingAssignment.onDemandPeriods || [];
            
            // Determine the overall operating period to span both service types
            const mergedStartTime = compareTimes(onDemandStart, fixedAssignment.operatingPeriod.startTime) < 0
              ? onDemandStart
              : fixedAssignment.operatingPeriod.startTime;
            const mergedEndTime = compareTimes(onDemandEnd, fixedAssignment.operatingPeriod.endTime) > 0
              ? onDemandEnd
              : fixedAssignment.operatingPeriod.endTime;
            
            const mergedAssignment = {
              ...fixedAssignment,
              operatingPeriod: {
                startTime: mergedStartTime,
                endTime: mergedEndTime,
              },
              onDemandPeriods: [...existingOnDemandPeriods, ...onDemandPeriods],
            } as VehicleAssignment & {
              onDemandPeriods?: Array<{ start: string; end: string; breaks: BreakPeriod[]; serviceType: 'on-demand' }>;
            };
            console.log(`[Mixed Fleet Optimizer] MERGING: ${onDemandAssignment.vehicleName} (${onDemandStart}-${onDemandEnd}) into ${fixedAssignment.vehicleName} (${fixedAssignment.operatingPeriod.startTime}-${fixedAssignment.operatingPeriod.endTime})`);
            mixedAssignments[i] = mergedAssignment;
            merged = true;
            break;
          }
        
        // Case 2: On-demand starts after fixed ends (sequential)
        // Allow exact match (fixed ends exactly when on-demand starts)
        if (onDemandStartMinutes >= fixedEnd - transitionBuffer) {
          // Check if periods don't overlap
          if (onDemandStartMinutes >= fixedEnd) {
            // Merge: combine on-demand periods into fixed route vehicle
            const existingAssignment = mixedAssignments[i] as VehicleAssignment & {
              onDemandPeriods?: Array<{ start: string; end: string; breaks: BreakPeriod[]; serviceType: 'on-demand' }>;
            };
            const existingOnDemandPeriods = existingAssignment.onDemandPeriods || [];
            
            const mergedAssignment = {
              ...fixedAssignment,
              operatingPeriod: {
                startTime: compareTimes(onDemandStart, fixedAssignment.operatingPeriod.startTime) < 0
                  ? onDemandStart
                  : fixedAssignment.operatingPeriod.startTime,
                endTime: compareTimes(onDemandEnd, fixedAssignment.operatingPeriod.endTime) > 0
                  ? onDemandEnd
                  : fixedAssignment.operatingPeriod.endTime,
              },
              onDemandPeriods: [...existingOnDemandPeriods, ...onDemandPeriods],
            } as VehicleAssignment & {
              onDemandPeriods?: Array<{ start: string; end: string; breaks: BreakPeriod[]; serviceType: 'on-demand' }>;
            };
            console.log(`[Mixed Fleet Optimizer] MERGING: ${onDemandAssignment.vehicleName} (${onDemandStart}-${onDemandEnd}) into ${fixedAssignment.vehicleName} (${fixedAssignment.operatingPeriod.startTime}-${fixedAssignment.operatingPeriod.endTime})`);
            mixedAssignments[i] = mergedAssignment;
            merged = true;
            break;
          } else {
            console.log(`[Mixed Fleet Optimizer] SKIP: Overlap detected - on-demand ends at ${onDemandEndMinutes}, fixed starts at ${fixedStart}`);
          }
        }
      }
      
      if (!merged) {
        console.log(`[Mixed Fleet Optimizer] NO MERGE: Could not merge ${onDemandAssignment.vehicleName} - keeping as separate vehicle`);
        remainingOnDemand.push(onDemandAssignment);
      }
    }
  }
  
  // Add any remaining on-demand assignments that couldn't be merged
  mixedAssignments.push(...remainingOnDemand);
  
  return mixedAssignments;
}

/**
 * Compare two time strings (HH:MM format)
 * Returns negative if time1 < time2, positive if time1 > time2, 0 if equal
 */
function compareTimes(time1: string, time2: string): number {
  const minutes1 = parseTime(time1);
  const minutes2 = parseTime(time2);
  return minutes1 - minutes2;
}

/**
 * Allocate fixed routes to gaps in on-demand assignments
 */
function allocateFixedRoutesToOnDemand(
  onDemandAssignments: VehicleAssignment[],
  fixedAssignments: VehicleAssignment[],
  gaps: Array<{ vehicleId: string; start: string; end: string }>,
  transitionTimeMinutes: number
): VehicleAssignment[] {
  const onDemandAssignmentsWithPeriods = onDemandAssignments as Array<VehicleAssignment & {
    onDemandPeriods?: Array<{ start: string; end: string; breaks: BreakPeriod[]; serviceType: 'on-demand' }>;
  }>;
  
  // Try to merge sequential, non-overlapping assignments
  const mixedAssignments: VehicleAssignment[] = [...onDemandAssignmentsWithPeriods];
  const remainingFixed: VehicleAssignment[] = [];
  
  // Merge sequential, non-overlapping periods to minimize total vehicles
  for (const fixedAssignment of fixedAssignments) {
    const fixedStart = parseTime(fixedAssignment.operatingPeriod.startTime);
    const fixedEnd = parseTime(fixedAssignment.operatingPeriod.endTime);
    
    let merged = false;
    
    // Try to find an on-demand vehicle with sequential periods
    for (let i = 0; i < mixedAssignments.length; i++) {
      const onDemandAssignment = mixedAssignments[i] as typeof onDemandAssignmentsWithPeriods[0];
      const onDemandPeriods = onDemandAssignment.onDemandPeriods || [];
      if (onDemandPeriods.length === 0) continue;
      
      // Find the earliest start and latest end of on-demand periods
      let onDemandStart = onDemandPeriods[0].start;
      let onDemandEnd = onDemandPeriods[0].end;
      onDemandPeriods.forEach(period => {
        if (compareTimes(period.start, onDemandStart) < 0) {
          onDemandStart = period.start;
        }
        if (compareTimes(period.end, onDemandEnd) > 0) {
          onDemandEnd = period.end;
        }
      });
      
      const onDemandStartMinutes = parseTime(onDemandStart);
      const onDemandEndMinutes = parseTime(onDemandEnd);
      
      // Check for sequential periods
      const transitionBuffer = transitionTimeMinutes;
      
      // Case 1: Fixed ends before on-demand starts (sequential)
      if (fixedEnd <= onDemandStartMinutes + transitionBuffer) {
        if (fixedEnd <= onDemandStartMinutes) {
          // Merge: combine fixed route into on-demand vehicle
          const mergedAssignment = {
            ...onDemandAssignment,
            routeAssignments: [...fixedAssignment.routeAssignments],
            operatingPeriod: {
              startTime: compareTimes(fixedAssignment.operatingPeriod.startTime, onDemandStart) < 0
                ? fixedAssignment.operatingPeriod.startTime
                : onDemandStart,
              endTime: compareTimes(fixedAssignment.operatingPeriod.endTime, onDemandEnd) > 0
                ? fixedAssignment.operatingPeriod.endTime
                : onDemandEnd,
            },
          } as VehicleAssignment & {
            onDemandPeriods?: Array<{ start: string; end: string; breaks: BreakPeriod[]; serviceType: 'on-demand' }>;
          };
          mixedAssignments[i] = mergedAssignment;
          merged = true;
          break;
        }
      }
      
      // Case 2: Fixed starts after on-demand ends (sequential)
      if (fixedStart >= onDemandEndMinutes - transitionBuffer) {
        if (fixedStart >= onDemandEndMinutes) {
          // Merge: combine fixed route into on-demand vehicle
          const mergedAssignment = {
            ...onDemandAssignment,
            routeAssignments: [...fixedAssignment.routeAssignments],
            operatingPeriod: {
              startTime: compareTimes(fixedAssignment.operatingPeriod.startTime, onDemandStart) < 0
                ? fixedAssignment.operatingPeriod.startTime
                : onDemandStart,
              endTime: compareTimes(fixedAssignment.operatingPeriod.endTime, onDemandEnd) > 0
                ? fixedAssignment.operatingPeriod.endTime
                : onDemandEnd,
            },
          } as VehicleAssignment & {
            onDemandPeriods?: Array<{ start: string; end: string; breaks: BreakPeriod[]; serviceType: 'on-demand' }>;
          };
          mixedAssignments[i] = mergedAssignment;
          merged = true;
          break;
        }
      }
    }
    
    if (!merged) {
      remainingFixed.push(fixedAssignment);
    }
  }
  
  // Add any remaining fixed assignments that couldn't be merged
  mixedAssignments.push(...remainingFixed);
  
  return mixedAssignments;
}

/**
 * Build unified timeline of all service requirements
 */
function buildUnifiedTimeline(
  fixedAssignments: VehicleAssignment[],
  trips: RideSchema[] | Line[],
  serviceHours: { start: string; end: string },
  onDemandVehicleCount: number
): Record<string, any> {
  // Build a timeline showing when each service type needs vehicles
  // This is a simplified implementation
  return {
    fixed: fixedAssignments,
    onDemand: {
      trips,
      vehicleCount: onDemandVehicleCount,
      serviceHours,
    },
  };
}

/**
 * Optimize unified allocation to minimize total vehicles
 */
function optimizeUnifiedAllocation(
  timeline: Record<string, any>,
  fixedAssignments: VehicleAssignment[],
  trips: RideSchema[] | Line[],
  serviceHours: { start: string; end: string },
  transitionTimeMinutes: number,
  config: MixedFleetConfig
): VehicleAssignment[] {
  // Simplified greedy algorithm: try to combine fixed route and on-demand assignments
  // where possible
  const optimizer = new FleetOptimizer();
  
  // For now, merge both types
  const onDemandResult = optimizer.calculateOnDemandFleet(trips, {
    mode: 'trip-layer',
    manualVehicleCount: timeline.onDemand.vehicleCount,
    vehicleSharingMode: 'dedicated',
    serviceStartTime: serviceHours.start || config.serviceStartTime || '06:00',
    serviceEndTime: serviceHours.end || config.serviceEndTime || '22:00',
    maxShiftTimeMinutes: 480,
    breakLengthMinutes: 30,
    minTimeBeforeBreakMinutes: 120,
    minTimeAfterBreakMinutes: 120,
    standardSeats: config.vehicleSpecs.standardSeats || 10,
    wheelchairSeats: config.vehicleSpecs.wheelchairSeats || 1,
    startLocationId: config.vehicleSpecs.startLocationId || '',
    endLocationId: config.vehicleSpecs.endLocationId || '',
    peakPeriods: [],
    breakReductionDuringPeakMinutes: 0,
    staggerBreaks: true,
    tripsPerVehiclePerHour: 5,
    useDemandBasedAllocation: config.useDemandBasedAllocation || false,
    hourlyVehicleRequirements: config.hourlyVehicleRequirements,
    flexibilityPercent: 0,
  });
  
  // TODO: Implement smarter merging algorithm
  return [...fixedAssignments, ...onDemandResult.vehicleAssignments];
}

/**
 * Allocate assignments with weighted priority
 */
function allocateWeightedAssignments(
  fixedAssignments: VehicleAssignment[],
  trips: RideSchema[] | Line[],
  serviceHours: { start: string; end: string },
  allocatedFixedVehicles: number,
  allocatedOnDemandVehicles: number,
  transitionTimeMinutes: number,
  config: MixedFleetConfig
): VehicleAssignment[] {
  // Use weighted allocation to create assignments
  const optimizer = new FleetOptimizer();
  
  // Take subset of fixed assignments based on allocation
  const selectedFixedAssignments = fixedAssignments.slice(0, allocatedFixedVehicles);
  
  // Create on-demand assignments
  const onDemandResult = optimizer.calculateOnDemandFleet(trips, {
    mode: 'trip-layer',
    manualVehicleCount: allocatedOnDemandVehicles,
    vehicleSharingMode: 'dedicated',
    serviceStartTime: serviceHours.start || config.serviceStartTime || '06:00',
    serviceEndTime: serviceHours.end || config.serviceEndTime || '22:00',
    maxShiftTimeMinutes: 480,
    breakLengthMinutes: 30,
    minTimeBeforeBreakMinutes: 120,
    minTimeAfterBreakMinutes: 120,
    standardSeats: config.vehicleSpecs.standardSeats || 10,
    wheelchairSeats: config.vehicleSpecs.wheelchairSeats || 1,
    startLocationId: config.vehicleSpecs.startLocationId || '',
    endLocationId: config.vehicleSpecs.endLocationId || '',
    peakPeriods: [],
    breakReductionDuringPeakMinutes: 0,
    staggerBreaks: true,
    tripsPerVehiclePerHour: 5,
    useDemandBasedAllocation: config.useDemandBasedAllocation || false,
    hourlyVehicleRequirements: config.hourlyVehicleRequirements,
    flexibilityPercent: 0,
  });
  
  return [...selectedFixedAssignments, ...onDemandResult.vehicleAssignments];
}

/**
 * Build mixed fleet summary statistics
 */
function buildMixedFleetSummary(
  assignments: VehicleAssignment[],
  fixedVehicleCount: number,
  onDemandVehicleCount: number,
  priorityMode: 'fixed-first' | 'on-demand-first' | 'equal' | 'weighted',
  routesServed: number,
  serviceHours: { start: string; end: string }
): any {
  // Count vehicles by service type
  let vehiclesFixedOnly = 0;
  let vehiclesOnDemandOnly = 0;
  let vehiclesMixed = 0;
  
  assignments.forEach((assignment) => {
    const hasFixed = assignment.routeAssignments.length > 0;
    const hasOnDemand = (assignment as any).onDemandPeriods && (assignment as any).onDemandPeriods.length > 0;
    
    if (hasFixed && hasOnDemand) {
      vehiclesMixed++;
    } else if (hasFixed) {
      vehiclesFixedOnly++;
    } else if (hasOnDemand) {
      vehiclesOnDemandOnly++;
    }
  });
  
  // Calculate service hours (simplified)
  const totalFixedRouteHours = calculateTotalServiceHours(assignments, 'fixed');
  const totalOnDemandHours = calculateTotalServiceHours(assignments, 'on-demand');
  const totalVehicleHours = calculateTotalVehicleHours(assignments);
  
  const utilizationPercent = totalVehicleHours > 0 
    ? ((totalFixedRouteHours + totalOnDemandHours) / totalVehicleHours) * 100 
    : 0;
  
  return {
    priorityMode,
    totalVehicles: assignments.length,
    vehiclesFixedOnly,
    vehiclesOnDemandOnly,
    vehiclesMixed,
    totalFixedRouteHours,
    totalOnDemandHours,
    totalVehicleHours,
    utilizationPercent,
    serviceTypeBreakdown: {
      fixedRoutePercent: totalVehicleHours > 0 ? (totalFixedRouteHours / totalVehicleHours) * 100 : 0,
      onDemandPercent: totalVehicleHours > 0 ? (totalOnDemandHours / totalVehicleHours) * 100 : 0,
    },
    allFixedRouteRequirementsMet: vehiclesFixedOnly + vehiclesMixed >= fixedVehicleCount,
    onDemandCoveragePercent: vehiclesOnDemandOnly + vehiclesMixed >= onDemandVehicleCount 
      ? 100 
      : ((vehiclesOnDemandOnly + vehiclesMixed) / onDemandVehicleCount) * 100,
    transitionCount: countTransitions(assignments),
  };
}

/**
 * Calculate total service hours for a specific service type
 */
function calculateTotalServiceHours(
  assignments: VehicleAssignment[],
  serviceType: 'fixed' | 'on-demand'
): number {
  let totalHours = 0;
  
  assignments.forEach((assignment) => {
    if (serviceType === 'fixed') {
      assignment.routeAssignments.forEach((routeAssignment) => {
        const start = parseTime(routeAssignment.startTime);
        const end = parseTime(routeAssignment.endTime);
        totalHours += calculateTimeDifference(start, end) / 60;
      });
    } else {
      const onDemandPeriods = (assignment as any).onDemandPeriods || [];
      onDemandPeriods.forEach((period: any) => {
        const start = parseTime(period.start);
        const end = parseTime(period.end);
        totalHours += calculateTimeDifference(start, end) / 60;
      });
    }
  });
  
  return totalHours;
}

/**
 * Calculate total vehicle hours
 */
function calculateTotalVehicleHours(assignments: VehicleAssignment[]): number {
  let totalHours = 0;
  
  assignments.forEach((assignment) => {
    const start = parseTime(assignment.operatingPeriod.startTime);
    const end = parseTime(assignment.operatingPeriod.endTime);
    totalHours += calculateTimeDifference(start, end) / 60;
  });
  
  return totalHours;
}

/**
 * Create transitions between fixed route and on-demand periods within a single shift
 * 
 * @param fixedAssignment - Fixed route assignment
 * @param onDemandPeriod - On-demand period to transition to/from
 * @param transitionTimeMinutes - Transition time in minutes
 * @param direction - 'fixed-to-on-demand' or 'on-demand-to-fixed'
 * @returns Transition result with updated periods and validation
 */
export function createTransition(
  fixedAssignment: VehicleAssignment | null,
  onDemandPeriod: { start: string; end: string } | null,
  transitionTimeMinutes: number,
  direction: 'fixed-to-on-demand' | 'on-demand-to-fixed'
): {
  fixedPeriod: { start: string; end: string } | null;
  onDemandPeriod: { start: string; end: string } | null;
  transitionStart: string;
  transitionEnd: string;
  isValid: boolean;
  errors: string[];
} {
  const errors: string[] = [];
  
  if (direction === 'fixed-to-on-demand') {
    if (!fixedAssignment || !onDemandPeriod) {
      errors.push('Both fixed assignment and on-demand period are required for fixed-to-on-demand transition');
      return {
        fixedPeriod: null,
        onDemandPeriod: null,
        transitionStart: '',
        transitionEnd: '',
        isValid: false,
        errors,
      };
    }
    
    // Calculate fixed route end time
    const fixedEnd = fixedAssignment.routeAssignments.length > 0
      ? fixedAssignment.routeAssignments[fixedAssignment.routeAssignments.length - 1].endTime
      : fixedAssignment.operatingPeriod.endTime;
    
    const fixedEndMinutes = parseTime(fixedEnd);
    const transitionStartMinutes = fixedEndMinutes;
    const transitionEndMinutes = addMinutes(fixedEndMinutes, transitionTimeMinutes);
    const onDemandStartMinutes = transitionEndMinutes;
    const onDemandEndMinutes = parseTime(onDemandPeriod.end);
    
    // Validate transition
    if (onDemandStartMinutes > onDemandEndMinutes) {
      errors.push(`Transition time causes on-demand period to start after its end time`);
    }
    
    if (transitionTimeMinutes < 0) {
      errors.push('Transition time cannot be negative');
    }
    
    return {
      fixedPeriod: {
        start: fixedAssignment.operatingPeriod.startTime,
        end: fixedEnd,
      },
      onDemandPeriod: {
        start: formatTime(onDemandStartMinutes),
        end: onDemandPeriod.end,
      },
      transitionStart: formatTime(transitionStartMinutes),
      transitionEnd: formatTime(transitionEndMinutes),
      isValid: errors.length === 0,
      errors,
    };
  } else {
    // on-demand-to-fixed
    if (!onDemandPeriod || !fixedAssignment) {
      errors.push('Both on-demand period and fixed assignment are required for on-demand-to-fixed transition');
      return {
        fixedPeriod: null,
        onDemandPeriod: null,
        transitionStart: '',
        transitionEnd: '',
        isValid: false,
        errors,
      };
    }
    
    // Calculate on-demand end time
    const onDemandEndMinutes = parseTime(onDemandPeriod.end);
    const transitionStartMinutes = onDemandEndMinutes;
    const transitionEndMinutes = addMinutes(onDemandEndMinutes, transitionTimeMinutes);
    const fixedStartMinutes = transitionEndMinutes;
    
    // Get fixed route start time requirement
    const fixedStartRequired = fixedAssignment.routeAssignments.length > 0
      ? parseTime(fixedAssignment.routeAssignments[0].startTime)
      : parseTime(fixedAssignment.operatingPeriod.startTime);
    
    // Validate transition
    if (fixedStartMinutes > fixedStartRequired + 60) { // Allow 1 hour flexibility
      errors.push(`Transition causes fixed route to start too late (more than 1 hour after required start)`);
    }
    
    if (transitionTimeMinutes < 0) {
      errors.push('Transition time cannot be negative');
    }
    
    return {
      fixedPeriod: {
        start: formatTime(fixedStartMinutes),
        end: fixedAssignment.routeAssignments.length > 0
          ? fixedAssignment.routeAssignments[fixedAssignment.routeAssignments.length - 1].endTime
          : fixedAssignment.operatingPeriod.endTime,
      },
      onDemandPeriod: {
        start: onDemandPeriod.start,
        end: onDemandPeriod.end,
      },
      transitionStart: formatTime(transitionStartMinutes),
      transitionEnd: formatTime(transitionEndMinutes),
      isValid: errors.length === 0,
      errors,
    };
  }
}

/**
 * Create a mixed assignment with transitions between fixed routes and on-demand periods
 * 
 * @param vehicleId - Vehicle identifier
 * @param vehicleName - Vehicle name
 * @param fixedPeriods - Array of fixed route periods
 * @param onDemandPeriods - Array of on-demand periods
 * @param transitionTimeMinutes - Transition time in minutes
 * @returns VehicleAssignment with mixed periods
 */
export function createMixedAssignment(
  vehicleId: string,
  vehicleName: string,
  fixedPeriods: Array<{ start: string; end: string; routeAssignments: any[] }>,
  onDemandPeriods: Array<{ start: string; end: string }>,
  transitionTimeMinutes: number
): VehicleAssignment & { 
  onDemandPeriods?: Array<{ start: string; end: string; breaks: BreakPeriod[]; serviceType: 'on-demand' }>;
  transitions?: Array<{ from: string; to: string; start: string; end: string }>;
} {
  // Sort periods by start time
  const allPeriods = [
    ...fixedPeriods.map(p => ({ ...p, type: 'fixed' as const })),
    ...onDemandPeriods.map(p => ({ ...p, type: 'on-demand' as const, routeAssignments: [] })),
  ].sort((a, b) => {
    const aStart = parseTime(a.start);
    const bStart = parseTime(b.start);
    return aStart - bStart;
  });
  
  // Find earliest start and latest end
  let earliestStart: string | null = null;
  let latestEnd: string | null = null;
  
  allPeriods.forEach((period) => {
    if (!earliestStart || compareTimes(period.start, earliestStart) < 0) {
      earliestStart = period.start;
    }
    if (!latestEnd || compareTimes(period.end, latestEnd) > 0) {
      latestEnd = period.end;
    }
  });
  
  // Collect all route assignments from fixed periods
  const routeAssignments = fixedPeriods.flatMap(p => p.routeAssignments);
  
  // Create transitions array
  const transitions: Array<{ from: string; to: string; start: string; end: string }> = [];
  for (let i = 0; i < allPeriods.length - 1; i++) {
    const current = allPeriods[i];
    const next = allPeriods[i + 1];
    
    if (current.type !== next.type) {
      // Transition between different service types
      const transitionStart = parseTime(current.end);
      const transitionEnd = addMinutes(transitionStart, transitionTimeMinutes);
      
      transitions.push({
        from: current.type,
        to: next.type,
        start: formatTime(transitionStart),
        end: formatTime(transitionEnd),
      });
    }
  }
  
  return {
    vehicleId,
    vehicleName,
    routeAssignments,
    operatingPeriod: {
      startTime: earliestStart || '00:00',
      endTime: latestEnd || '23:59',
    },
    onDemandPeriods: onDemandPeriods.map(p => ({
      start: p.start,
      end: p.end,
      breaks: [],
      serviceType: 'on-demand' as const,
    })),
    transitions,
  };
}

/**
 * Validate transitions in a vehicle assignment
 * 
 * @param assignment - Vehicle assignment to validate
 * @returns Validation result
 */
export function validateTransitions(
  assignment: VehicleAssignment & { 
    onDemandPeriods?: Array<{ start: string; end: string }>;
    transitions?: Array<{ from: string; to: string; start: string; end: string }>;
  }
): { isValid: boolean; errors: string[]; warnings: string[] } {
  const errors: string[] = [];
  const warnings: string[] = [];
  
  // Collect all periods (fixed and on-demand)
  const periods: Array<{ start: string; end: string; type: string }> = [];
  
  // Add fixed route periods
  assignment.routeAssignments.forEach((routeAssignment) => {
    periods.push({
      start: routeAssignment.startTime,
      end: routeAssignment.endTime,
      type: 'fixed',
    });
  });
  
  // Add on-demand periods
  const onDemandPeriods = (assignment as any).onDemandPeriods || [];
  onDemandPeriods.forEach((period: any) => {
    periods.push({
      start: period.start,
      end: period.end,
      type: 'on-demand',
    });
  });
  
  // Sort by start time
  periods.sort((a, b) => {
    const aStart = parseTime(a.start);
    const bStart = parseTime(b.start);
    return aStart - bStart;
  });
  
  // Check for overlaps
  for (let i = 0; i < periods.length - 1; i++) {
    const current = periods[i];
    const next = periods[i + 1];
    
    const currentEnd = parseTime(current.end);
    const nextStart = parseTime(next.start);
    
    if (nextStart < currentEnd) {
      errors.push(
        `Overlapping periods detected: ${current.type} (${current.start}-${current.end}) ` +
        `overlaps with ${next.type} (${next.start}-${next.end})`
      );
    }
    
    // Check for gaps (warn if gap is significant and not a transition)
    const gap = nextStart - currentEnd;
    if (gap > 60) { // More than 1 hour gap
      warnings.push(
        `Large gap detected between ${current.type} (${current.end}) ` +
        `and ${next.type} (${next.start}): ${Math.round(gap / 60)} hours`
      );
    }
  }
  
  // Validate transitions
  const transitions = (assignment as any).transitions || [];
  transitions.forEach((transition: any, index: number) => {
    const transitionStart = parseTime(transition.start);
    const transitionEnd = parseTime(transition.end);
    
    if (transitionEnd < transitionStart) {
      errors.push(`Transition ${index + 1} has invalid time range: ${transition.start} to ${transition.end}`);
    }
    
    const transitionDuration = transitionEnd - transitionStart;
    if (transitionDuration > 120) { // More than 2 hours seems unreasonable
      warnings.push(`Transition ${index + 1} duration is very long: ${Math.round(transitionDuration / 60)} hours`);
    }
  });
  
  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
}

/**
 * Count transitions between service types
 */
function countTransitions(assignments: VehicleAssignment[]): number {
  let transitions = 0;
  
  assignments.forEach((assignment) => {
    const transitionsArray = (assignment as any).transitions || [];
    transitions += transitionsArray.length;
    
    // Fallback: if no transitions array, count vehicles with both types
    if (transitionsArray.length === 0) {
      const hasFixed = assignment.routeAssignments.length > 0;
      const hasOnDemand = (assignment as any).onDemandPeriods && (assignment as any).onDemandPeriods.length > 0;
      
      if (hasFixed && hasOnDemand) {
        transitions++;
      }
    }
  });
  
  return transitions;
}

